import { useEffect, useMemo, type ReactNode } from "react";
import {
  parseNarrationBeats,
  worldImageSource,
  playerVisibleDocuments,
  type NarrationBeat,
  type SceneHeadingProps,
  type WorldDocument,
} from "@private-fiction/story-core";
import { AccountMediaImage } from "./AccountMedia";

interface NovelBeatRendererProps {
  source: string;
  documents?: readonly WorldDocument[];
  onSelectEntity?: ((document: WorldDocument) => void) | undefined;
  onSceneHeading?: ((heading: SceneHeadingProps) => void) | undefined;
  className?: string;
}

export function NovelBeatRenderer({
  source,
  documents = [],
  onSelectEntity,
  onSceneHeading,
  className = "",
}: NovelBeatRendererProps) {
  const beats = useMemo(() => parseNarrationBeats(source), [source]);

  // Find locations, characters, items map for entity linking
  const entityMap = useMemo(() => {
    const map = new Map<string, WorldDocument>();
    for (const doc of playerVisibleDocuments(documents)) {
      if (doc.title) map.set(doc.title.toLowerCase().trim(), doc);
      const slug = doc.title.toLowerCase().replace(/\s+/g, "-");
      map.set(slug, doc);
      if (doc.id) map.set(doc.id, doc);
      if (typeof doc.fields.slug === "string") map.set(doc.fields.slug.toLowerCase(), doc);
    }
    return map;
  }, [documents]);

  // Trigger onSceneHeading when a SceneHeading beat is detected
  useEffect(() => {
    const headingBeat = beats.find((b) => b.type === "SceneHeading");
    if (headingBeat && headingBeat.type === "SceneHeading" && onSceneHeading) {
      onSceneHeading(headingBeat.props);
    }
  }, [beats, onSceneHeading]);

  if (!beats.length) {
    if (/```(?:spec|beats)/i.test(source)) return null;
    return <p className={`novel-narration ${className}`}>{source}</p>;
  }

  // Helper to render text with entity links
  const renderTextWithEntityLinks = (text: string): ReactNode => {
    if (!documents.length || !onSelectEntity) return text;
    // Highlight known entity titles in text
    const titles = playerVisibleDocuments(documents)
      .map((d) => d.title.trim())
      .filter((t) => t.length >= 2)
      .sort((a, b) => b.length - a.length);

    if (!titles.length) return text;

    const regex = new RegExp(`(${titles.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "g");
    const parts = text.split(regex);

    return parts.map((part, i) => {
      const matchDoc = entityMap.get(part.toLowerCase().trim());
      if (matchDoc) {
        return (
          <span
            key={i}
            className={`novel-entity-link entity-kind--${matchDoc.kind}`}
            onClick={() => onSelectEntity(matchDoc)}
            title={`เปิดดู ${matchDoc.title} (${matchDoc.kind})`}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div className={`novel-beat-stream ${className}`}>
      {beats.map((beat, index) => {
        switch (beat.type) {
          case "SceneHeading": {
            const locDoc = beat.props.locationSlug
              ? entityMap.get(beat.props.locationSlug.toLowerCase())
              : entityMap.get(beat.props.title.toLowerCase());
            const coverImage = worldImageSource(locDoc);

            return (
              <header key={index} className="novel-scene-heading">
                {coverImage && (
                  <div className="novel-scene-heading__banner">
                    <AccountMediaImage source={coverImage} alt={beat.props.title} />
                  </div>
                )}
                <div className="novel-scene-heading__divider">
                  <span className="divider-line" />
                  <strong
                    className="scene-title"
                    onClick={() => locDoc && onSelectEntity && onSelectEntity(locDoc)}
                  >
                    {beat.props.title}
                  </strong>
                  <span className="divider-line" />
                </div>
                {beat.props.subtitle && (
                  <div className="novel-scene-heading__subtitle">
                    {beat.props.subtitle}
                  </div>
                )}
              </header>
            );
          }

          case "Dialogue": {
            const speakerDoc = beat.props.slug
              ? entityMap.get(beat.props.slug.toLowerCase())
              : entityMap.get(beat.props.name.toLowerCase());
            const avatar = worldImageSource(speakerDoc);

            return (
              <div key={index} className="novel-dialogue-line">
                <div
                  className="novel-dialogue__speaker"
                  onClick={() => speakerDoc && onSelectEntity && onSelectEntity(speakerDoc)}
                  title={speakerDoc ? `ดูข้อมูล ${beat.props.name}` : undefined}
                >
                  {avatar && (
                    <AccountMediaImage
                      source={avatar}
                      alt={beat.props.name}
                      className="novel-dialogue__avatar"
                    />
                  )}
                  <span className="novel-dialogue__name">{beat.props.name}</span>
                </div>
                <div className="novel-dialogue__speech">
                  “{renderTextWithEntityLinks(beat.props.text)}”
                </div>
              </div>
            );
          }

          case "Handout":
            return (
              <aside
                key={index}
                className={`novel-handout style--${beat.props.style || "letter"}`}
              >
                {beat.props.title && <strong>{beat.props.title}</strong>}
                <div className="novel-handout__body">
                  {renderTextWithEntityLinks(beat.props.text)}
                </div>
              </aside>
            );

          case "ReadAloud":
            return (
              <blockquote key={index} className="novel-read-aloud">
                {renderTextWithEntityLinks(beat.props.text)}
              </blockquote>
            );

          case "ActionReceipt":
            return (
              <div key={index} className="novel-action-receipt">
                <span className="receipt-spark">✦</span>
                <span className="receipt-text">
                  บันทึกข้อมูล {beat.props.count} รายการ
                </span>
                {beat.props.changes.map((c, ci) => (
                  <span key={ci} className="receipt-tag">
                    {c.title}
                  </span>
                ))}
              </div>
            );

          case "GmNarration":
          default:
            return (
              <p key={index} className="novel-narration">
                {renderTextWithEntityLinks(beat.props.text)}
              </p>
            );
        }
      })}
    </div>
  );
}
